import { useState } from "react";
import axios from "axios";
import {
  CheckCircle, SealCheck, X, PaperPlaneTilt, Receipt, DownloadSimple,
} from "@phosphor-icons/react";
import { http, API, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Renders the approval state / CTA for a milestone task.
 * mode: "auth" (client logged in) or "public" (share-token viewer)
 * task must have: { id, status, milestone_amount, approval, invoice }
 * shareToken required in "public" mode
 */
export default function MilestoneApproveButton({ task, mode = "auth", shareToken, onApproved }) {
  const [open, setOpen] = useState(false);
  const [clientName, setClientName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!task.is_milestone) return null;

  const invoiceUrl = task.invoice?.number
    ? (mode === "public"
        ? `${API}/public/projects/${shareToken}/invoices/${task.id}/html`
        : `${API}/invoices/${task.id}/html`)
    : null;

  // Already approved → show invoice CTA
  if (task.approval?.approved) {
    return (
      <div className="mt-3 border-l-4 border-emerald-600 bg-emerald-50 px-4 py-3" data-testid={`approval-shown-${task.id}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="label-overline text-emerald-700 flex items-center gap-1.5">
              <SealCheck size={12} weight="bold" /> Approved
              {task.approval.approved_by && (
                <span className="text-emerald-900 normal-case tracking-normal"> · {task.approval.approved_by}</span>
              )}
            </div>
            <div className="text-xs text-zinc-600 mt-1 font-mono">
              {task.approval.approved_at && new Date(task.approval.approved_at).toLocaleString()}
            </div>
            {task.approval.note && (
              <div className="text-sm text-zinc-800 mt-2 whitespace-pre-wrap">&quot;{task.approval.note}&quot;</div>
            )}
          </div>
          {invoiceUrl && (
            <a
              href={invoiceUrl}
              target="_blank" rel="noopener noreferrer"
              data-testid={`view-invoice-${task.id}`}
              className="shrink-0 inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 text-white px-4 py-2 label-overline hover:bg-blue-600 hover:border-blue-600"
            >
              <Receipt size={12} weight="bold" />
              Invoice {task.invoice.number}
              <DownloadSimple size={12} weight="bold" />
            </a>
          )}
        </div>
      </div>
    );
  }

  // Not yet approved — but only complete milestones can be approved
  if (task.status !== "complete") {
    return (
      <div className="mt-3 text-xs text-zinc-500 font-mono flex items-center gap-1.5">
        <CheckCircle size={11} weight="bold" /> Approval unlocks once studio marks this milestone complete
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "public" && !clientName.trim()) {
      toast.error("Please type your name to approve"); return;
    }
    setSubmitting(true);
    try {
      const body = { note: note.trim() };
      if (mode === "public") {
        body.client_name = clientName.trim();
        await axios.post(`${API}/public/projects/${shareToken}/milestones/${task.id}/approve`, body);
      } else {
        await http.post(`/client/milestones/${task.id}/approve`, body);
      }
      toast.success("Milestone approved · invoice unlocked");
      setOpen(false); setClientName(""); setNote("");
      if (onApproved) onApproved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid={`approve-open-${task.id}`}
          className="inline-flex items-center gap-2 border-2 border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-600 hover:text-white px-4 py-2 label-overline transition-colors"
        >
          <SealCheck size={12} weight="bold" /> Approve & Release Next Phase
        </button>
      ) : (
        <form onSubmit={submit} className="border-l-4 border-emerald-600 border-y border-r border-zinc-300 bg-white p-4 space-y-3"
              data-testid={`approve-form-${task.id}`}>
          <div className="flex items-center justify-between">
            <div className="label-overline text-emerald-700">Confirm Approval</div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="text-zinc-400 hover:text-zinc-900">
              <X size={14} weight="bold" />
            </button>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed">
            You&apos;re approving <strong>&quot;{task.title}&quot;</strong>. This locks the milestone as accepted and
            unlocks an invoice for ₹ {task.milestone_amount?.toLocaleString("en-IN")}.
          </p>
          {mode === "public" && (
            <div>
              <label className="label-overline block mb-1.5 text-zinc-700">Your Name (for accountability) *</label>
              <input
                required maxLength={120}
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                data-testid={`approve-name-${task.id}`}
                placeholder="e.g. Rajesh Kumar"
                className="w-full rounded-none border border-zinc-400 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          )}
          <div>
            <label className="label-overline block mb-1.5 text-zinc-700">Note (optional)</label>
            <textarea
              rows={2} maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid={`approve-note-${task.id}`}
              placeholder="Any final feedback or acknowledgment…"
              className="w-full rounded-none border border-zinc-400 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 resize-y"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={() => setOpen(false)}
                    className="label-overline text-zinc-500 hover:text-zinc-900 px-3 py-2">Cancel</button>
            <button type="submit" disabled={submitting}
                    data-testid={`approve-submit-${task.id}`}
                    className="inline-flex items-center gap-2 label-overline bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 disabled:opacity-60">
              <PaperPlaneTilt size={12} weight="bold" />
              {submitting ? "Approving…" : "Confirm Approval"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
